import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, ImagePlus, Trash2, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import ProjectForm from "@/components/ProjectForm";
import { uploadFile } from "@/components/project/PhotosTab";
import ShareKit from "@/components/project/ShareKit";
import { BinderCard, ClientViewCard, TradesCard } from "@/components/project/SiteCards";
import { Spinner } from "@/components/States";

export const inviteLink = (token) => `${window.location.origin}/invite/${token}`;

export function copy(text) {
  navigator.clipboard?.writeText(text).then(() => toast.success("Link copied"), () => toast.message(text));
}

function InviteBox({ project, reload }) {
  const [f, setF] = useState({ email: project.client_email || "", name: project.client_name || "", role: "client" });
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/invites", { ...f, project_id: project.id });
      if (data.existing_user) { toast.success("Existing user added to this project"); reload(); }
      else { setLink(inviteLink(data.token)); if (data.emailed) toast.success(`Invitation emailed to ${data.email}`); else toast.message("Email couldn't be sent. Share the link below instead."); }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="space-y-3 rounded-xl border bg-card p-5" data-testid="project-invite-form">
      <p className="eyebrow">Invite to this project</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="project-invite-email" />
        <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="project-invite-name" />
      </div>
      <div className="flex gap-2">
        {["client", "crew"].map((r) => <Button key={r} type="button" size="sm" variant={f.role === r ? "default" : "outline"} onClick={() => setF({ ...f, role: r })} data-testid={`project-invite-role-${r}`} className="capitalize">{r}</Button>)}
        <Button type="submit" size="sm" className="ml-auto rounded-full" disabled={busy} data-testid="project-invite-submit">{busy && <Spinner />}Send invitation</Button>
      </div>
      {link && (
        <div className="flex items-center gap-2 rounded-md bg-secondary p-2" data-testid="project-invite-link">
          <code className="flex-1 truncate text-xs">{link}</code>
          <Button type="button" size="sm" variant="ghost" onClick={() => copy(link)} data-testid="copy-invite-link"><Copy className="h-4 w-4" /></Button>
        </div>
      )}
    </form>
  );
}

export default function SettingsTab({ project, onSaved, reload }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const strip = ({ members, id, created_at, created_by, crew_ids, client_ids, workspace_id, ...rest }) => rest;
  const save = async (payload) => {
    try { const { data } = await api.put(`/projects/${project.id}`, payload); onSaved({ ...project, ...data }); toast.success("Project saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setHero = async (file) => {
    setBusy(true);
    try {
      const f = await uploadFile(file, project.id, "hero");
      const { members, id, created_at, created_by, crew_ids, client_ids, ...rest } = project;
      await save({ ...rest, hero_image: f.url });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const removeMember = async (m) => {
    try { await api.delete(`/projects/${project.id}/members/${m.id}`); toast.success("Removed from project"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    try { await api.delete(`/projects/${project.id}`); toast.success("Project deleted"); nav("/projects"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-8 xl:grid-cols-[1fr_380px]">
      <div className="rounded-xl border bg-card p-6"><ProjectForm key={project.id} initial={project} onSubmit={save} /></div>
      <div className="space-y-6">
        <div className="space-y-3 rounded-xl border bg-card p-5">
          <p className="eyebrow">Hero image</p>
          {project.hero_image && <img src={fileUrl(project.hero_image)} alt="" className="aspect-video w-full rounded-md object-cover" />}
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm underline-offset-4 hover:underline" data-testid="hero-upload-label">
            {busy ? <Spinner /> : <ImagePlus className="h-4 w-4" />} Upload hero image
            <input type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && setHero(e.target.files[0])} data-testid="hero-file-input" />
          </label>
        </div>
        <InviteBox project={project} reload={reload} />
        <ClientViewCard project={project} onSave={(patch) => save({ ...strip(project), ...patch })} />
        <TradesCard project={project} />
        <BinderCard project={project} onSave={(patch, silent) => (silent ? onSaved({ ...project, ...patch }) : save({ ...strip(project), ...patch }))} />
        <ShareKit project={project} />
        <div className="space-y-2 rounded-xl border bg-card p-5" data-testid="project-members">
          <p className="eyebrow">People on this project</p>
          {(project.members || []).length === 0 && <p className="text-sm text-muted-foreground">No crew or clients yet.</p>}
          {(project.members || []).map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate">{m.name || m.email} <Badge variant="outline" className="ml-1 capitalize">{m.role}</Badge></span>
              <Button size="icon" variant="ghost" aria-label="Remove" onClick={() => removeMember(m)} data-testid={`remove-member-${m.id}`}><UserMinus className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="outline" className="text-destructive" data-testid="delete-project-button"><Trash2 className="mr-1 h-4 w-4" />Delete project</Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete {project.name}?</AlertDialogTitle><AlertDialogDescription>This removes the project and its board. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} data-testid="confirm-delete-project">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
